import { GridApi } from '@ag-grid-community/core';
import { isCustomSortEnabled } from 'common/visualizations/helpers/VifSelectors';
import { HierarchyColumnConfig, OrderConfig, SortDirection } from 'common/visualizations/vif';
import { GROUP_COLUMN_PREFIX, HEADER_SORT_CYCLE } from '../Constants';
import { getGroupedOrderConfig, getSortDirection } from './SortHelpers';
import { renderIcon } from './sortIconHelpers';
import { isEqual, map, filter } from 'lodash';

export type RenderUnindentedHeaderProps = {
  colId?: string;
  sortToFollow?: SortDirection;
  isIndented: boolean | undefined;
  gridRefWrapper: React.RefObject<HTMLDivElement>;
  gridApi: GridApi | null;
  hierarchyColumns: HierarchyColumnConfig[];
  vifOrderConfig: OrderConfig[];
};

export const renderUnindentedHeader = (props: RenderUnindentedHeaderProps) => {
  const { colId, sortToFollow, isIndented, gridRefWrapper, gridApi, hierarchyColumns, vifOrderConfig } =
    props;
  if (!isCustomSortEnabled() || isIndented) return;
  const raf = requestAnimationFrame(() => {
    const gridElement = gridRefWrapper.current;
    if (!gridElement || !gridApi) return;

    const iconFactory: Record<string, () => Element> = {
      ascending: () => renderIcon('arrow_upward'),
      descending: () => renderIcon('arrow_downward'),
      customSort: () => renderIcon('swap_vert'),
    };
    const headers = Array.from(gridElement.getElementsByClassName('ag-header-cell'));
    const clearSortIcons = (container: HTMLDivElement) => {
      Array.from(container.children).forEach((child, index) => {
        if (index > 0) {
          container.removeChild(child);
        }
      });
    };
    const appendIcon = (container: HTMLDivElement, direction?: SortDirection) => {
      if (!direction) return;
      const icon = iconFactory[direction]?.();
      if (icon) container.appendChild(icon);
    };
    if (colId) {
      const header = headers.find((h) => h.getAttribute('col-id') === `${GROUP_COLUMN_PREFIX}-${colId}`);
      if (!header) return;
      const container = header
        .getElementsByClassName('ag-sort-indicator-container')
        .item(0) as HTMLDivElement;
      if (!container) return;
      clearSortIcons(container);
      appendIcon(container, sortToFollow);
      return;
    }
    const groupedOrderConfig = getGroupedOrderConfig({
      hierarchies: hierarchyColumns,
      orderConfig: vifOrderConfig,
    });
    if (!groupedOrderConfig) return;
    const targetColIds = groupedOrderConfig.map((o) => `${GROUP_COLUMN_PREFIX}-${o.columnName}`);
    headers
      .filter((header) => targetColIds.includes(header.getAttribute('col-id') || ''))
      .forEach((header) => {
        const colIdValue = header.getAttribute('col-id')?.replace(`${GROUP_COLUMN_PREFIX}-`, '');
        if (!colIdValue) return;

        const config = groupedOrderConfig.find((o) => o.columnName === colIdValue);
        if (!config) return;

        const container = header
          .getElementsByClassName('ag-sort-indicator-container')
          .item(0) as HTMLDivElement;
        if (!container) return;

        clearSortIcons(container);
        appendIcon(container, getSortDirection(config));
      });
  });
  return () => cancelAnimationFrame(raf);
};

export type GetHeaderElementProps = {
  gridRefWrapper: React.RefObject<HTMLDivElement>;
  gridApi: GridApi | null;
};

export const getHeaderElement = (props: GetHeaderElementProps) => {
  const { gridApi, gridRefWrapper } = props;
  const gridElement = gridRefWrapper.current;
  if (!gridElement || !gridApi) return;
  const headers = gridElement.getElementsByClassName('ag-header-cell ag-column-first');
  if (headers.length !== 1) return;
  const header = headers.item(0) as HTMLDivElement;
  if (header.getAttribute('col-id') !== GROUP_COLUMN_PREFIX) return;
  return header;
};

export type RenderIndentedHeaderProps = {
  isIndented: boolean | undefined;
  headerSortCycleIndex: number;
  gridRefWrapper: React.RefObject<HTMLDivElement>;
  gridApi: GridApi | null;
  sortSourceElement: string | null;
  vifOrderConfig: OrderConfig[];
  hierarchyColumns: HierarchyColumnConfig[];
};

export const renderIndentedHeader = (props: RenderIndentedHeaderProps) => {
  const {
    isIndented,
    headerSortCycleIndex,
    gridApi,
    gridRefWrapper,
    sortSourceElement,
    vifOrderConfig,
    hierarchyColumns,
  } = props;
  if (!isCustomSortEnabled() && !isIndented) return;
  const raf = requestAnimationFrame(() => {
    const header = getHeaderElement({ gridApi, gridRefWrapper });
    if (!header) return;
    const container = header.getElementsByClassName('ag-sort-indicator-container').item(0) as HTMLDivElement;
    if (!container) return;
    const iconMap: Record<string, string> = {
      ascending: 'eSortAsc',
      descending: 'eSortDesc',
      mixed: 'eSortMixed',
    };
    let currentSort = null;
    const groupedColumnNames = filter(hierarchyColumns, (column) => column.isGrouping).map(
      (column) => column.columnName,
    );
    const groupedOrderConfig = filter(vifOrderConfig, (config) =>
      groupedColumnNames.includes(config.columnName),
    );
    const sortToFollows = map(groupedOrderConfig, 'sortToFollow');
    const sortValues = [...new Set(sortToFollows)];
    // If sorting is triggered via Row Groups sections, the following icon will be shown based on these criteria:
    // - If all row groups are sorted in ascending order, the header icon should be `arrow_upward`.
    // - If all row groups are sorted in descending order, the header icon should be `arrow_downward`.
    // - If the row groups have a mixed sorting configuration (asc, desc, mixed), the header icon should be `hamburger`.
    // - If none of the row groups have a sorting configuration, no header icon will be shown.

    if (sortValues.length === 1 && isEqual(sortToFollows.length, groupedColumnNames.length)) {
      if (sortSourceElement == 'header') {
        currentSort = HEADER_SORT_CYCLE[headerSortCycleIndex];
      } else {
        currentSort = sortValues[0];
        if (currentSort === 'customSort') {
          return;
        }
      }
    }

    if (!currentSort) return;
    const iconRef = iconMap[currentSort as string] ?? null;
    const children = Array.from(container.children);
    children.forEach((child, index) => {
      if (index === 0) return;
      const dataRef = child.getAttribute('data-ref');
      if (dataRef === iconRef) {
        child.classList.remove('ag-hidden');
      } else {
        child.classList.add('ag-hidden');
      }
    });
  });
  return () => cancelAnimationFrame(raf);
};

type RefreshIndentedHeaderProps = {
  gridRefWrapper: React.RefObject<HTMLDivElement>;
  gridApi: GridApi | null;
};

export const refreshIndentedHeader = (props: RefreshIndentedHeaderProps) => {
  const { gridRefWrapper, gridApi } = props;
  const raf = requestAnimationFrame(() => {
    const header = getHeaderElement({ gridApi, gridRefWrapper });
    if (!header) return;
    const resizers = header.getElementsByClassName('ag-header-cell-resize');
    if (resizers.length !== 1) return;
    (resizers.item(0) as HTMLDivElement).click();
  });
  return () => cancelAnimationFrame(raf);
};

export type AssignAriaSortToTableHeaders = {
  groupedColumns: string[];
  isIndented: boolean;
  gridRefWrapper: React.RefObject<HTMLDivElement>;
  headerSortCycleIndex: number;
  sortState: OrderConfig[] | [];
};

/*
    Function used to assign aria-sort attribute value to table headers depending on their sort state.
  */
export const assignAriaSortToTableHeaders = (props: AssignAriaSortToTableHeaders): void => {
  const { groupedColumns, isIndented, gridRefWrapper, headerSortCycleIndex, sortState } = props;
  const ariaSortValues = ['none', 'ascending', 'descending', 'other'];
  const gridElement = gridRefWrapper.current;
  if (!gridElement) return;

  const hasSort = sortState && sortState.length > 0;
  const hasGroupedColumns = groupedColumns.length > 0;
  if (!isIndented && !hasSort) return;

  // Assign aria-sort for indented grouped column header
  if (isIndented) {
    const groupedColHeader = gridRefWrapper.current?.querySelector(
      `.ag-header-cell[col-id="${GROUP_COLUMN_PREFIX}"]`,
    );
    if (groupedColHeader) {
      groupedColHeader.setAttribute('aria-sort', ariaSortValues[headerSortCycleIndex]);
    }
  }

  if (!hasSort) return;

  // Assign aria-sort for all sorted columns
  sortState?.forEach(({ columnName, sortToFollow }: OrderConfig) => {
    const isGrouped = groupedColumns.includes(columnName);
    const colId = hasGroupedColumns && isGrouped ? `${GROUP_COLUMN_PREFIX}-${columnName}` : columnName;
    const colHeader = gridRefWrapper.current?.querySelector(`.ag-header-cell[col-id="${colId}"]`);
    if (colHeader) {
      let ariaSort: string;
      switch (sortToFollow) {
        case 'ascending':
        case 'descending':
          ariaSort = sortToFollow;
          break;
        case 'customSort':
          ariaSort = 'other';
          break;
        case undefined:
        case null:
        case 'default':
        default:
          ariaSort = 'none';
      }
      colHeader.setAttribute('aria-sort', ariaSort);
    }
  });
};
